import { useState } from 'react'
import { format } from 'date-fns'
import type { N8nIngestRun, N8nIngestRunItem } from '@/hooks/useN8nIngestRuns'
import {
  useN8nIngestIdempotentFiles,
  useResetN8nIngestFileIdempotency,
} from '@/hooks/useN8nIngestIdempotentFiles'
import { AdminSectionCard } from '@/components/listings-management/AdminSectionCard'

interface IngestHistorySectionProps {
  runs: N8nIngestRun[]
  isLoading: boolean
  isError: boolean
  enabled: boolean
}

function isAlreadyProcessedSkip(item: N8nIngestRunItem): boolean {
  return (
    item.status === 'skipped' &&
    !!item.error_summary &&
    /already processed/i.test(item.error_summary)
  )
}

export function IngestHistorySection({ runs, isLoading, isError, enabled }: IngestHistorySectionProps) {
  const {
    data: idempotentFiles = [],
    isLoading: idempotentLoading,
    isError: idempotentError,
  } = useN8nIngestIdempotentFiles(enabled)
  const resetMutation = useResetN8nIngestFileIdempotency()
  const [resettingFileId, setResettingFileId] = useState<string | null>(null)
  const [resetMessage, setResetMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const handleAllowReprocess = async (srcFileId: string | null, fileName?: string | null) => {
    if (!srcFileId) {
      setResetMessage({ type: 'error', text: 'No Drive file id on this item — cannot reset idempotency.' })
      return
    }

    setResettingFileId(srcFileId)
    setResetMessage(null)
    try {
      const deleted = await resetMutation.mutateAsync(srcFileId)
      setResetMessage({
        type: 'success',
        text:
          deleted > 0
            ? `${fileName ?? srcFileId} will be processed on the next refresh (cleared ${deleted} successful ingest record${deleted === 1 ? '' : 's'}).`
            : `${fileName ?? srcFileId} was not marked as successfully processed — nothing to clear.`,
      })
    } catch (err) {
      const detail = err instanceof Error ? err.message : 'Could not reset file idempotency'
      const hint = /could not find the function/i.test(detail)
        ? ' Run migration 114_n8n_ingest_file_idempotency_reset.sql in Supabase.'
        : ''
      setResetMessage({ type: 'error', text: `${detail}${hint}` })
    } finally {
      setResettingFileId(null)
    }
  }

  return (
    <AdminSectionCard id="history" title="Ingest history">
      <p className="mb-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
        Audit log for Refresh listings runs. Files marked as successfully processed are skipped on future runs unless
        you allow reprocess below or use Force re-process on Refresh.
      </p>

      <div
        className="mb-6 p-4 rounded border"
        style={{ borderColor: 'var(--bg-tertiary)', backgroundColor: 'var(--bg-primary)' }}
      >
        <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
          Drive files marked processed
        </h3>
        <p className="mb-3 text-xs" style={{ color: 'var(--text-secondary)' }}>
          These Google Drive files have a successful ingest record. The n8n workflow skips them until you clear that
          record. Use this when a file ran through the workflow but data did not appear correctly in the app.
        </p>

        {idempotentLoading && (
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }} role="status">
            Loading processed files…
          </p>
        )}

        {idempotentError && (
          <p className="text-sm" style={{ color: 'var(--color-error)' }} role="alert">
            Could not load processed files. Ensure migration 114 is applied.
          </p>
        )}

        {!idempotentLoading && !idempotentError && idempotentFiles.length === 0 && (
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            No Drive files are currently blocking idempotency.
          </p>
        )}

        {!idempotentLoading && idempotentFiles.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr style={{ color: 'var(--text-secondary)' }}>
                  <th className="text-left py-2 pr-3 font-medium">File</th>
                  <th className="text-left py-2 pr-3 font-medium">Stream</th>
                  <th className="text-left py-2 pr-3 font-medium">Last processed</th>
                  <th className="text-left py-2 pr-3 font-medium">Rows</th>
                  <th className="text-left py-2 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {idempotentFiles.map((file) => (
                  <tr key={file.src_file_id} style={{ borderTop: '1px solid var(--bg-tertiary)' }}>
                    <td className="py-2 pr-3" style={{ color: 'var(--text-primary)' }}>
                      {file.src_file_name ?? file.src_file_id}
                    </td>
                    <td className="py-2 pr-3" style={{ color: 'var(--text-secondary)' }}>
                      {file.stream ?? '—'}/{file.ingest_type}
                    </td>
                    <td className="py-2 pr-3" style={{ color: 'var(--text-secondary)' }}>
                      {file.last_finished_at
                        ? format(new Date(file.last_finished_at), 'dd MMM yyyy HH:mm')
                        : '—'}
                    </td>
                    <td className="py-2 pr-3" style={{ color: 'var(--text-secondary)' }}>
                      {file.rows_inserted ?? '—'}
                    </td>
                    <td className="py-2">
                      <button
                        type="button"
                        onClick={() => void handleAllowReprocess(file.src_file_id, file.src_file_name)}
                        disabled={resetMutation.isPending}
                        className="px-2 py-1 rounded border text-xs"
                        style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
                      >
                        {resettingFileId === file.src_file_id ? 'Clearing…' : 'Allow reprocess'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {resetMessage && (
        <p
          className="mb-4 text-sm"
          role="alert"
          style={{ color: resetMessage.type === 'error' ? 'var(--color-error)' : 'var(--text-primary)' }}
        >
          {resetMessage.text}
        </p>
      )}

      {isLoading && (
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }} role="status">
          Loading ingest history…
        </p>
      )}

      {isError && (
        <p className="text-sm" style={{ color: 'var(--color-error)' }} role="alert">
          Could not load ingest history. Ensure migration 105 is applied.
        </p>
      )}

      {!isLoading && !isError && runs.length === 0 && (
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>No ingest runs yet.</p>
      )}

      {!isLoading && runs.length > 0 && (
        <div className="space-y-4">
          {runs.map((run) => (
            <div
              key={run.id}
              className="p-4 rounded border"
              style={{ borderColor: 'var(--bg-tertiary)', backgroundColor: 'var(--bg-primary)' }}
            >
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm mb-2">
                <span style={{ color: 'var(--text-primary)' }}>
                  <strong>{run.status}</strong>
                  {run.created_at && ` · ${format(new Date(run.created_at), 'dd MMM yyyy HH:mm')}`}
                </span>
                <span style={{ color: 'var(--text-secondary)' }}>
                  Files: {run.files_succeeded}/{run.files_total} ok
                  {run.files_failed > 0 && `, ${run.files_failed} failed`}
                  {run.files_skipped > 0 && `, ${run.files_skipped} skipped`}
                </span>
                {run.epg_sync_status && (
                  <span style={{ color: 'var(--text-secondary)' }}>EPG: {run.epg_sync_status}</span>
                )}
              </div>
              {run.error_summary && (
                <p className="text-sm mb-2" style={{ color: 'var(--color-error)' }} role="alert">
                  {run.error_summary}
                </p>
              )}
              {run.n8n_ingest_run_items && run.n8n_ingest_run_items.length > 0 && (
                <ul className="text-xs space-y-2 mt-2 list-none p-0 m-0">
                  {run.n8n_ingest_run_items.map((item) => {
                    const showReprocess =
                      !!item.src_file_id && (item.status === 'success' || isAlreadyProcessedSkip(item))

                    return (
                      <li
                        key={item.id}
                        className="flex flex-wrap items-start justify-between gap-2"
                        style={{ color: 'var(--text-secondary)' }}
                      >
                        <span>
                          <span style={{ color: 'var(--text-primary)' }}>{item.status}</span>
                          {' · '}
                          {item.src_file_name ?? item.src_file_id ?? 'unknown file'}
                          {item.stream && ` (${item.stream}/${item.ingest_type})`}
                          {item.error_summary && (
                            <span style={{ color: 'var(--color-error)' }}> — {item.error_summary}</span>
                          )}
                        </span>
                        {showReprocess && item.src_file_id && (
                          <button
                            type="button"
                            onClick={() => void handleAllowReprocess(item.src_file_id, item.src_file_name)}
                            disabled={resetMutation.isPending}
                            className="shrink-0 px-2 py-0.5 rounded border text-xs"
                            style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
                          >
                            {resettingFileId === item.src_file_id ? 'Clearing…' : 'Allow reprocess'}
                          </button>
                        )}
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </AdminSectionCard>
  )
}
